import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import * as imageDB from '../utils/imageDB'
import { ImageResource } from './ImageResource'

/**
 * Single reactive owner of the image library.
 *
 * Every image is represented by exactly one `ImageResource` (./ImageResource.js) —
 * there is no separate `Map<uuid, HTMLImageElement>` or similar parallel
 * cache; the resource IS the cache. Widgets (see HaspImage.js) look up a
 * resource via `getResourceByFilename`, call `resource.ensureDecoded()`,
 * and read `resource.image`/`resource.loading`/`resource.error` reactively
 * — they never decode or own image bytes themselves.
 *
 * Persistence still delegates to the existing IndexedDB layer
 * (src/utils/imageDB.js) unchanged; `load()` reconciles fresh rows into
 * the existing `ImageResource` instances (creating new ones only for
 * genuinely new rows) so a refresh never discards an already-decoded
 * image or in-flight decode.
 */
export const useImageStore = defineStore('images', {
  state: () => ({
    resources: [], // ImageResource[] — the single runtime owner of all designer-side image state
    labels: [],
    loaded: false, // true once load() has completed at least once
  }),
  getters: {
    all: (state) => state.resources,
    allLabels: (state) => state.labels,
    getImage: (state) => (id) => state.resources.find((r) => r.id === id) ?? null,
    // Synchronous, reactive-lookup-safe (usable inside a Vue watch getter) —
    // the lookups HaspImage uses to find its shared resource. By uuid is the
    // primary path; by filename exists for backward compatibility with
    // designs saved before widgets carried a uuid reference.
    getResourceByUuid: (state) => (uuid) => state.resources.find((r) => r.uuid === uuid) ?? null,
    getResourceByFilename: (state) => (filename) => state.resources.find((r) => r.filename === filename) ?? null,
  },
  actions: {
    // --- Refresh — the single source of truth every mutating action re-syncs from ---

    async load() {
      const rows = await imageDB.loadImages()
      this.labels = await imageDB.getAllLabels()

      // Backfill uuid for rows that predate it, persisting immediately so
      // it's stable across reloads instead of being regenerated every time.
      for (const row of rows) {
        if (!row.uuid) {
          row.uuid = uuidv4()
          await imageDB.updateImageInDB(row)
        }
      }

      // Reconcile by uuid: reuse existing ImageResource instances (and
      // their decoded image/loading/error state) for rows that still
      // exist; only construct new resources for genuinely new rows.
      const existingByUuid = new Map(this.resources.map((r) => [r.uuid, r]))
      this.resources = rows.map((row) => {
        const existing = existingByUuid.get(row.uuid)
        if (existing) {
          existing._syncFromRow(row)
          return existing
        }
        return markRaw(new ImageResource(row))
      })
      this.loaded = true
    },

    /** Lazily load-once, memoizing concurrent callers onto the same in-flight load. */
    async ensureLoaded() {
      if (this.loaded) return
      if (!this._loadPromise) {
        this._loadPromise = this.load().finally(() => {
          this._loadPromise = null
        })
      }
      return this._loadPromise
    },

    /**
     * Wrap a non-library src (a remote URL, or a bare relative path like the
     * `img` widget's default placeholder) in the same ImageResource shape
     * everything else uses, so HaspImage never has a second, ad hoc decode
     * path for "not from the library" images. Deliberately NOT added to
     * `state.resources` and NOT deduplicated across calls — external images
     * never shared decode state before this refactor (mirroring firmware,
     * which also excludes non-filesystem images from its Image Resource
     * Manager), and this preserves that, rather than introducing new sharing
     * behavior for a case nobody asked to share.
     */
    acquireExternalResource(src, filename) {
      return markRaw(new ImageResource({ uuid: null, id: null, filename, labelId: null, labelName: null, imageSize: null, src }))
    },

    // --- Images ---

    async addImage(base64String, filename, labelId = null) {
      const id = await imageDB.addImageToDB(base64String, filename, labelId)
      await this.load()
      return id
    },
    async removeImage(id) {
      await imageDB.deleteImage(id)
      await this.load()
    },
    async clearImages() {
      await imageDB.clearImages()
      await this.load()
    },
    async getImageByFilename(filename) {
      const resource = this.getResourceByFilename(filename)
      if (resource) return resource
      return imageDB.fetchImageByFilename(filename) // fallback: store not loaded yet
    },
    async setImageLabelId(imageId, labelId) {
      await imageDB.setImageLabelId(imageId, labelId)
      await this.load()
    },
    async duplicateImage(imageId) {
      await imageDB.copyImage(imageId)
      await this.load()
    },
    async updateImage(image) {
      await imageDB.updateImageInDB(image)
      await this.load()
      return image
    },

    // --- Labels ---

    async addLabel(name, parentId = null) {
      const label = await imageDB.addLabel(name, parentId)
      await this.load()
      return label
    },
    async getAllLabels() {
      return imageDB.getAllLabels()
    },
    async getLabelById(id) {
      return imageDB.getLabelById(id)
    },
    async updateLabelParent(labelId, newParentId) {
      await imageDB.updateLabelParent(labelId, newParentId)
      await this.load()
    },
    async deleteLabelAndChildren(id) {
      await imageDB.deleteLabelAndChildren(id)
      await this.load() // also self-heals any images left pointing at the deleted label(s)
    },
    async collectDescendantIds(id) {
      return imageDB.collectDescendantIds(id)
    },
    async isDescendant(labels, draggedId, targetId) {
      return imageDB.isDescendant(labels, draggedId, targetId)
    },
    async getLabelPathArray(id) {
      return imageDB.getLabelPathArray(id)
    },
    async getLabelPathString(id) {
      return imageDB.getLabelPathString(id)
    },
    async getLabelByPathArray(pathArray) {
      const label = await imageDB.getLabelByPathArray(pathArray)
      await this.load() // getLabelByPathArray can create new labels as a side effect
      return label
    },
  },
})
